import React, { useState, useEffect } from 'react';
import { Plus, Settings, X, Filter, Edit } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import EnquiryDetailsModal from './EnquiryDetailsModal';
import EnquiryForm from './EnquiryForm';

import api from '../../services/api';

interface EnquiryForm {
  source: 'whatsapp' | 'email' | 'agent_portal';
  agency_id: number | undefined;
  customer_name: string;
  customer_reference: string;
  pic_name: string;
  travel_date: string;
  plan_text: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  number_of_adults: number;
  number_of_kids: number;
  number_of_nights: number;
}

interface Agency {
  agency_id: number;
  agency_name: string;
  agency_email: string;
  status: string;
}

interface MasterData {
  agencies: Agency[];
}

interface Enquiry {
  enquiry_id: number;
  enquiry_request_id?: number;
  agency_name: string;
  customer_name: string;
  customer_reference: string;
  pic_name: string;
  status: string;
  priority: string;
  travel_date: string;
  created_at: string;
  updated_at?: string;
  plan_text: string;
  request_history?: any[];
  itineraries?: any[];
  rework_count?: number;
  request_id?: number;
  number_of_adults?: number;
  number_of_kids?: number;
  number_of_nights?: number;
  kids_ages?: any;
}

const CreateEnquiry: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [formData, setFormData] = useState<EnquiryForm>({
    source: 'agent_portal',
    agency_id: undefined,
    customer_name: '',
    customer_reference: '',
    pic_name: '',
    travel_date: '',
    plan_text: '',
    priority: 'medium',
    number_of_adults: 0,
    number_of_kids: 0,
    number_of_nights: 0
  });
  const [kidsAges, setKidsAges] = useState<number[]>([]);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [allEnquiries, setAllEnquiries] = useState<Enquiry[]>([]);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    createdFromDate: new Date().toISOString().split('T')[0],
    createdToDate: new Date().toISOString().split('T')[0],
    travelFromDate: '',
    travelToDate: '',
    agencyName: '',
    enquiryId: '',
    requestId: '',
    itineraryId: '',
    quotationId: '',
    customerName: '',
    customerReference: '',
    picName: '',
    status: '',
    priority: '',
    assignedTo: ''
  });
  const [sortConfig, setSortConfig] = useState<{field: string, direction: 'asc' | 'desc'}>({field: 'created_at', direction: 'desc'});

  useEffect(() => {
    if (showViewModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showViewModal]);

  useEffect(() => {
    fetchMasterData();
    fetchEnquiries();
  }, []);

  const handleSort = (field: string) => {
    const newDirection = sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field, direction: newDirection });
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) {
      return <span className="text-gray-400">↕️</span>;
    }
    return sortConfig.direction === 'asc' ? <span className="text-blue-600">↑</span> : <span className="text-blue-600">↓</span>;
  };

  const sortedEnquiries = React.useMemo(() => {
    return [...enquiries].sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortConfig.field) {
        case 'enquiry_id':
          aValue = a.enquiry_id;
          bValue = b.enquiry_id;
          break;
        case 'agency_name':
          aValue = a.agency_name?.toLowerCase() || '';
          bValue = b.agency_name?.toLowerCase() || '';
          break;
        case 'status':
          const statusOrder = { pending: 1, assigned: 2, in_progress: 3, completed: 4 };
          aValue = statusOrder[a.status as keyof typeof statusOrder] || 0;
          bValue = statusOrder[b.status as keyof typeof statusOrder] || 0;
          break;
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
          bValue = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
          break;
        case 'travel_date':
        case 'created_at':
        case 'updated_at':
          const aDateValue = a[sortConfig.field as keyof Enquiry];
          const bDateValue = b[sortConfig.field as keyof Enquiry];
          aValue = aDateValue ? new Date(aDateValue as string) : new Date(0);
          bValue = bDateValue ? new Date(bDateValue as string) : new Date(0);
          break;
        default:
          aValue = a[sortConfig.field as keyof Enquiry] || '';
          bValue = b[sortConfig.field as keyof Enquiry] || '';
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [enquiries, sortConfig]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/master-data/agencies');
      
      if (response.data && typeof response.data === 'object') {
        const agenciesArray = Object.entries(response.data).map(([name, id]) => ({
          agency_id: typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : 0),
          agency_name: name,
          agency_email: '',
          status: 'active'
        }));
        setMasterData({ agencies: agenciesArray });
      } else {
        setMasterData({ agencies: [] });
      }
    } catch (error: any) {
      console.error('Error fetching agencies:', error);
      setMasterData({ agencies: [] });
    } finally {
      setLoading(false);
    }
  };

  const fetchEnquiries = async (searchFilters?: typeof filters) => {
    try {
      const filtersToUse = searchFilters || filters;
      const params = new URLSearchParams();
      
      // Add non-empty filters as query parameters
      if (filtersToUse.enquiryId) params.append('enquiry_id', filtersToUse.enquiryId);
      if (filtersToUse.requestId) params.append('request_id', filtersToUse.requestId);
      if (filtersToUse.itineraryId) params.append('itinerary_id', filtersToUse.itineraryId);
      if (filtersToUse.quotationId) params.append('quotation_id', filtersToUse.quotationId);
      if (filtersToUse.agencyName) params.append('agency_name', filtersToUse.agencyName);
      if (filtersToUse.createdFromDate) params.append('created_from_date', filtersToUse.createdFromDate);
      if (filtersToUse.createdToDate) params.append('created_to_date', filtersToUse.createdToDate);
      if (filtersToUse.travelFromDate) params.append('travel_from_date', filtersToUse.travelFromDate);
      if (filtersToUse.travelToDate) params.append('travel_to_date', filtersToUse.travelToDate);
      if (filtersToUse.status) params.append('status', filtersToUse.status);
      if (filtersToUse.priority) params.append('priority', filtersToUse.priority);
      
      const queryString = params.toString();
      const url = queryString ? `/work-enquiries/all?${queryString}` : '/work-enquiries/all';
      
      const response = await api.get(url);
      if (response.data.success) {
        let data = response.data.data;
        
        // Apply client-side date filtering if backend doesn't handle it
        if (filtersToUse.createdFromDate || filtersToUse.createdToDate) {
          data = data.filter((e: Enquiry) => {
            // Parse the created_at date - it comes as UTC timestamp
            const createdDateStr = e.created_at.includes('Z') ? e.created_at : e.created_at + 'Z';
            const createdDate = new Date(createdDateStr);
            
            if (filtersToUse.createdFromDate) {
              const fromDate = new Date(filtersToUse.createdFromDate + 'T00:00:00');
              if (createdDate < fromDate) return false;
            }
            if (filtersToUse.createdToDate) {
              const toDate = new Date(filtersToUse.createdToDate + 'T23:59:59.999');
              if (createdDate > toDate) return false;
            }
            return true;
          });
        }
        
        setAllEnquiries(data);
        setEnquiries(data);
      }
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('source', 'agent_portal');
      formDataToSend.append('agency_id', formData.agency_id?.toString() || '');
      formDataToSend.append('priority', formData.priority);
      formDataToSend.append('customer_name', formData.customer_name);
      formDataToSend.append('customer_reference', formData.customer_reference);
      formDataToSend.append('pic_name', formData.pic_name);
      formDataToSend.append('number_of_adults', formData.number_of_adults.toString());
      formDataToSend.append('number_of_kids', formData.number_of_kids.toString());
      formDataToSend.append('kids_ages', JSON.stringify(kidsAges));
      formDataToSend.append('travel_date', formData.travel_date);
      formDataToSend.append('number_of_nights', formData.number_of_nights.toString());
      formDataToSend.append('request_text', formData.plan_text);
      
      if (attachment) {
        formDataToSend.append('attachment', attachment);
      }
      
      const response = await api.post('/v2/enquiries', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data) {
        setFormData({
          source: 'agent_portal',
          agency_id: undefined,
          customer_name: '',
          customer_reference: '',
          pic_name: '',
          travel_date: '',
          plan_text: '',
          priority: 'medium',
          number_of_adults: 0,
          number_of_kids: 0,
          number_of_nights: 0
        });
        setKidsAges([]);
        setAttachment(null);
        alert('Enquiry created successfully!');
        setShowModal(false);
        fetchEnquiries();
      }
    } catch (error) {
      console.error('Error creating enquiry:', error);
      alert('Error creating enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof EnquiryForm, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyClientSideFilters = (currentFilters: typeof filters, data: Enquiry[]) => {
    let filtered = [...data];
    
    if (currentFilters.createdFromDate) {
      const fromDate = new Date(currentFilters.createdFromDate);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(e => new Date(e.created_at) >= fromDate);
    }
    if (currentFilters.createdToDate) {
      const toDate = new Date(currentFilters.createdToDate);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(e => new Date(e.created_at) <= toDate);
    }
    if (currentFilters.travelFromDate) {
      const fromDate = new Date(currentFilters.travelFromDate);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(e => new Date(e.travel_date) >= fromDate);
    }
    if (currentFilters.travelToDate) {
      const toDate = new Date(currentFilters.travelToDate);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(e => new Date(e.travel_date) <= toDate);
    }
    if (currentFilters.agencyName) {
      filtered = filtered.filter(e => e.agency_name.toLowerCase().includes(currentFilters.agencyName.toLowerCase()));
    }
    if (currentFilters.customerName) {
      filtered = filtered.filter(e => e.customer_name?.toLowerCase().includes(currentFilters.customerName.toLowerCase()));
    }
    if (currentFilters.customerReference) {
      filtered = filtered.filter(e => e.customer_reference?.toLowerCase().includes(currentFilters.customerReference.toLowerCase()));
    }
    if (currentFilters.picName) {
      filtered = filtered.filter(e => e.pic_name?.toLowerCase().includes(currentFilters.picName.toLowerCase()));
    }
    if (currentFilters.enquiryId) {
      filtered = filtered.filter(e => e.enquiry_id.toString().includes(currentFilters.enquiryId));
    }
    if (currentFilters.requestId) {
      filtered = filtered.filter(e => e.enquiry_request_id?.toString().includes(currentFilters.requestId));
    }
    if (currentFilters.itineraryId) {
      filtered = filtered.filter(e => {
        if (e.itineraries?.some(itin => itin.id?.toString().includes(currentFilters.itineraryId))) {
          return true;
        }
        const enquiryStr = JSON.stringify(e).toLowerCase();
        return enquiryStr.includes(currentFilters.itineraryId.toLowerCase());
      });
    }
    if (currentFilters.status) {
      filtered = filtered.filter(e => e.status === currentFilters.status);
    }
    if (currentFilters.priority) {
      filtered = filtered.filter(e => e.priority === currentFilters.priority);
    }
    
    setEnquiries(filtered);
  };

  // Apply client-side filters when filter inputs change
  // Skip if we just fetched from backend with filters (backend already filtered)
  useEffect(() => {
    const hasBackendFilters = filters.enquiryId || filters.requestId || filters.itineraryId || 
                              filters.quotationId ||
                              filters.createdFromDate || filters.createdToDate ||
                              filters.travelFromDate || filters.travelToDate || 
                              filters.status || filters.priority;
    if (!hasBackendFilters) {
      applyClientSideFilters(filters, allEnquiries);
    }
  }, [filters, allEnquiries]);

  const clearFilters = () => {
    const emptyFilters = {
      createdFromDate: new Date().toISOString().split('T')[0],
      createdToDate: new Date().toISOString().split('T')[0],
      travelFromDate: '',
      travelToDate: '',
      agencyName: '',
      enquiryId: '',
      requestId: '',
      itineraryId: '',
      quotationId: '',
      customerName: '',
      customerReference: '',
      picName: '',
      status: '',
      priority: '',
      assignedTo: ''
    };
    setFilters(emptyFilters);
    fetchEnquiries(emptyFilters);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-purple-100 text-purple-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      case 'assigned': return 'Assigned';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const handleViewEnquiry = async (enquiry: Enquiry) => {
    try {
      const response = await api.get(`/enquiry-details/${enquiry.enquiry_id}`);
      if (response.data.success) {
        const fullEnquiryData = { ...response.data.data, enquiry_id: enquiry.enquiry_id };
        setSelectedEnquiry(fullEnquiryData);
        setShowViewModal(true);
      } else {
        console.error('Failed to fetch enquiry details:', response.data);
        alert('Failed to load enquiry details');
      }
    } catch (error) {
      console.error('Error fetching enquiry details:', error);
      alert('Failed to load enquiry details');
    }
  };



  const handleUpdateEnquiry = async (updatedData: any) => {
    if (!selectedEnquiry) return;
    
    try {
      const response = await api.put(`/work-enquiries/${selectedEnquiry.enquiry_id}`, updatedData);
      if (response.data.success) {
        alert('Enquiry updated successfully!');
        setShowViewModal(false);
        fetchEnquiries();
      }
    } catch (error) {
      console.error('Error updating enquiry:', error);
      alert('Error updating enquiry. Please try again.');
    }
  };


  const agencyOptions = masterData?.agencies?.map(agency => ({
    value: agency.agency_id,
    label: agency.agency_name
  })) || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <div className="w-full px-6 py-4 flex-1 overflow-y-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Enquiry Management</h1>
            <p className="text-sm text-gray-500 mt-1">Manage all enquiries from agencies</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Enquiry
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Enquiries</p>
                <p className="text-2xl font-bold text-gray-900">{enquiries.length}</p>
              </div>
              <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm font-bold">📋</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{enquiries.filter(e => e.status === 'pending').length}</p>
              </div>
              <div className="h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center">
                <span className="text-orange-600 text-sm font-bold">⏳</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{enquiries.filter(e => e.status === 'in_progress').length}</p>
              </div>
              <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-sm font-bold">🔄</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Created Today</p>
                <p className="text-2xl font-bold text-purple-600">
                  {enquiries.filter(e => {
                    const today = new Date().toDateString();
                    const createdDate = new Date(e.created_at).toDateString();
                    return createdDate === today;
                  }).length}
                </p>
              </div>
              <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-sm font-bold">📅</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
          
          {showFilters && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Enquiry ID</label>
                  <input
                    type="text"
                    placeholder="Search by ID..."
                    value={filters.enquiryId}
                    onChange={(e) => setFilters(prev => ({ ...prev, enquiryId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Request ID</label>
                  <input
                    type="text"
                    placeholder="Search by request ID..."
                    value={filters.requestId}
                    onChange={(e) => setFilters(prev => ({ ...prev, requestId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Itinerary ID</label>
                  <input
                    type="text"
                    placeholder="Search by itinerary ID..."
                    value={filters.itineraryId}
                    onChange={(e) => setFilters(prev => ({ ...prev, itineraryId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quotation ID</label>
                  <input
                    type="text"
                    placeholder="Search by quotation..."
                    value={filters.quotationId}
                    onChange={(e) => setFilters(prev => ({ ...prev, quotationId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                  <input
                    type="text"
                    placeholder="Search by customer..."
                    value={filters.customerName}
                    onChange={(e) => setFilters(prev => ({ ...prev, customerName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                  <input
                    type="text"
                    placeholder="Search by reference..."
                    value={filters.customerReference}
                    onChange={(e) => setFilters(prev => ({ ...prev, customerReference: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PIC Name</label>
                  <input
                    type="text"
                    placeholder="Search by PIC..."
                    value={filters.picName}
                    onChange={(e) => setFilters(prev => ({ ...prev, picName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name</label>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={filters.agencyName}
                    onChange={(e) => setFilters(prev => ({ ...prev, agencyName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created From</label>
                  <input
                    type="date"
                    value={filters.createdFromDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, createdFromDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created To</label>
                  <input
                    type="date"
                    value={filters.createdToDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, createdToDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date From</label>
                  <input
                    type="date"
                    value={filters.travelFromDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, travelFromDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date To</label>
                  <input
                    type="date"
                    value={filters.travelToDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, travelToDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">All</option>
                    <option value="pending">Pending</option>
                    <option value="assigned">Assigned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={filters.priority}
                    onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">All</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div></div>
                <div></div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">Showing {enquiries.length} results</div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => fetchEnquiries(filters)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Search
                  </button>
                  <button 
                    onClick={clearFilters} 
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enquiries Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  onClick={() => handleSort('enquiry_id')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                >
                  <div className="flex items-center space-x-1">
                    <span>Enquiry ID</span>
                    {getSortIcon('enquiry_id')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request ID</th>
                <th 
                  onClick={() => handleSort('agency_name')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                >
                  <div className="flex items-center space-x-1">
                    <span>Agency / Customer</span>
                    {getSortIcon('agency_name')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('status')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {getSortIcon('status')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('travel_date')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                >
                  <div className="flex items-center space-x-1">
                    <span>Travel Date</span>
                    {getSortIcon('travel_date')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('created_at')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                >
                  <div className="flex items-center space-x-1">
                    <span>Created</span>
                    {getSortIcon('created_at')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('updated_at')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                >
                  <div className="flex items-center space-x-1">
                    <span>Updated</span>
                    {getSortIcon('updated_at')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedEnquiries.map((enquiry) => (
                <tr key={enquiry.enquiry_id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">#{enquiry.enquiry_id}</span>
                      {enquiry.rework_count && enquiry.rework_count > 0 && (
                        <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded font-bold" title={`${enquiry.rework_count} rework request(s)`}>
                          R{enquiry.rework_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <span className="font-mono text-gray-600">
                      #{enquiry.enquiry_request_id || 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <div className="font-medium text-gray-900 truncate max-w-[200px]" title={enquiry.agency_name}>{enquiry.agency_name}</div>
                    <div className="text-gray-500 text-xs truncate max-w-[200px]" title={enquiry.customer_name || '-'}>{enquiry.customer_name || '-'}</div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(enquiry.status)}`}>
                      {getStatusLabel(enquiry.status)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm">{new Date(enquiry.travel_date).toLocaleDateString('en-GB')}</td>
                  <td className="px-4 py-4 text-sm">{new Date(enquiry.created_at + 'Z').toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="px-4 py-4 text-sm">
                    {enquiry.updated_at ? 
                      new Date(enquiry.updated_at + 'Z').toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 
                      '-'
                    }
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleViewEnquiry(enquiry)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded" 
                        title="Manage"
                      >
                        <Settings className="h-4 w-4" />
                      </button>

                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {enquiries.length === 0 && (
            <div className="text-center py-12 text-gray-500">No enquiries found</div>
          )}
        </div>
      </div>

      {/* Shared Enquiry Details Modal */}
      <EnquiryDetailsModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        enquiry={selectedEnquiry}
        agencies={masterData?.agencies || []}
        onSave={handleUpdateEnquiry}
        onReworkCreated={() => {
          fetchEnquiries();
          if (selectedEnquiry) {
            handleViewEnquiry({ ...selectedEnquiry, enquiry_id: selectedEnquiry.enquiry_id || (selectedEnquiry as any).id } as Enquiry);
          }
        }}
      />



      {/* Create Enquiry Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-[95vw] h-[95vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Create New Enquiry</h2>
                <p className="text-sm text-gray-600 mt-1">Request-based enquiry system</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <EnquiryForm
                  formData={formData}
                  onFormDataChange={setFormData}
                  agencies={masterData?.agencies || []}
                  kidsAges={kidsAges}
                  onKidsAgesChange={setKidsAges}
                  attachment={attachment}
                  onAttachmentChange={setAttachment}
                  isEdit={false}
                />
              </form>
            </div>

            <div className="flex gap-3 p-6 border-t bg-gray-50">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                onClick={handleSubmit}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {isSubmitting ? 'Creating...' : 'Create Enquiry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateEnquiry;
